import type { SceneryChunk, SceneryRect } from '@/modules/game-protocol/world-scenery';

type Load = (chunkX:number,chunkY:number,signal:AbortSignal)=>Promise<SceneryChunk>;
const CELL=64, CAPACITY=128, CONCURRENCY=3, RETRY_MS=5000;

/** Driven by GameEngine's existing frame owner. No timer, RAF or renderer network access. */
export class SceneryStream {
  private readonly cache=new Map<string,SceneryChunk>();
  private readonly active=new Map<string,AbortController>();
  private readonly retryAt=new Map<string,number>();
  private desired=new Map<string,{x:number;y:number}>();
  private destroyed=false;
  private now=0;
  private published='';
  private revision='';

  constructor(private readonly load:Load,private readonly publish:(chunks:readonly SceneryChunk[])=>void) {}

  update(bounds:SceneryRect,now:number):void {
    if(this.destroyed||![bounds.minX,bounds.minY,bounds.maxX,bounds.maxY,now].every(Number.isFinite))return;
    this.now=now;
    const centerX=Math.floor((bounds.minX+bounds.maxX)/(CELL*2)),centerY=Math.floor((bounds.minY+bounds.maxY)/(CELL*2));
    const candidates:Array<{x:number;y:number}>=[];
    // Clamp before enumeration so an extreme overview cannot allocate an unbounded request queue.
    for(let x=Math.max(Math.floor(bounds.minX/CELL),centerX-16);x<=Math.min(Math.floor(bounds.maxX/CELL),centerX+16);x++) {
      for(let y=Math.max(Math.floor(bounds.minY/CELL),centerY-16);y<=Math.min(Math.floor(bounds.maxY/CELL),centerY+16);y++)candidates.push({x,y});
    }
    candidates.sort((a,b)=>(a.x-centerX)**2+(a.y-centerY)**2-((b.x-centerX)**2+(b.y-centerY)**2)||a.x-b.x||a.y-b.y);
    this.desired=new Map(candidates.slice(0,CAPACITY).map(point=>[`${point.x},${point.y}`,point]));
    for(const [key,controller]of this.active)if(!this.desired.has(key)){controller.abort();this.active.delete(key);}
    for(const key of this.retryAt.keys())if(!this.desired.has(key))this.retryAt.delete(key);
    this.emit();this.pump();
  }

  destroy():void {
    this.destroyed=true;
    for(const controller of this.active.values())controller.abort();
    this.active.clear();this.cache.clear();this.desired.clear();this.retryAt.clear();
  }

  private emit():void {
    const chunks=[...this.desired.keys()].flatMap(key=>this.cache.has(key)?[this.cache.get(key)!]:[]);
    const signature=chunks.map(chunk=>`${chunk.chunkX},${chunk.chunkY}:${chunk.revision}`).join('|');
    if(signature===this.published)return;
    this.published=signature;this.publish(Object.freeze(chunks));
  }

  private pump():void {
    if(this.destroyed)return;
    for(const [key,{x,y}]of this.desired){
      if(this.active.size>=CONCURRENCY)break;
      if(this.cache.has(key)||this.active.has(key)||(this.retryAt.get(key)??-Infinity)>this.now)continue;
      const controller=new AbortController();this.active.set(key,controller);
      void this.load(x,y,controller.signal).then(chunk=>{
        if(this.destroyed||controller.signal.aborted||this.active.get(key)!==controller||!this.desired.has(key))return;
        if(chunk.chunkX!==x||chunk.chunkY!==y||!chunk.revision)throw new Error('Invalid scenery chunk coordinates');
        if(this.revision&&this.revision!==chunk.revision){
          this.cache.clear();
          for(const [otherKey,other]of this.active)if(otherKey!==key){other.abort();this.active.delete(otherKey);}
        }
        this.revision=chunk.revision;
        this.cache.delete(key);this.cache.set(key,chunk);this.retryAt.delete(key);
        while(this.cache.size>CAPACITY){
          const evict=[...this.cache.keys()].find(candidate=>!this.desired.has(candidate))??this.cache.keys().next().value!;
          this.cache.delete(evict);
        }
        this.emit();
      }).catch(()=>{
        if(!this.destroyed&&!controller.signal.aborted)this.retryAt.set(key,this.now+RETRY_MS);
      }).finally(()=>{
        if(this.active.get(key)===controller)this.active.delete(key);
        this.pump();
      });
    }
  }
}
